import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { updateUserSessionTime, updateUserAiBasedAnalysis } from "../../store/slices/authSlice";
import settingsAPI from "../../services/settingsService";
import Table from "../../components/Common/Table/Table";
import ActionButtons from "../../components/Common/ActionButtons/ActionButtons";
import SkeletonLoader from "../../components/SkeletonLoader/SkeletonLoader";
import ListScreenPaginationBar from "../../components/Common/ListScreenPaginationBar/ListScreenPaginationBar";
import { useBreadcrumb } from "../../contexts/BreadcrumbContext";
import ChatAccess from "../ChatAccess/ChatAccess";
import EmailLogs from "../EmailLogs/EmailLogs";
import UserLogs from "../AdminManagement/UserLogs/UserLogs";
import MobileAppAccess from "../MobileAppAccess/MobileAppAccess";
import UserRoleManagement from "./UserRoleManagement/UserRoleManagement";
import SettingsHub, {
  SETTINGS_TAB_IDS,
  useSettingsTabs,
} from "./SettingsHub";
import SidebarMenuSettings from "./SidebarMenuSettings";
import "./Settings.css";
import { matchesWordStart, matchesWordStartAny } from "../../utils/searchMatch";
import { formatPhoneForDisplay } from "../../utils/phoneUtils";

// --- Follow Up Template Component ---
const FollowUpSetting = () => {
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({ name: "", template: "" });
  const [formErrors, setFormErrors] = useState({ name: "", template: "" });
  const [sortConfig, setSortConfig] = useState({ key: "id", direction: "asc" });

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
    setCurrentPage(1);
  };

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const response = await settingsAPI.getFollowUpTemplates();
      if (response.success) {
        setTemplates(response.data || []);
      }
    } catch (error) {
      toast.error(error.message || "Failed to fetch templates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    let filtered = templates;
    if (searchTerm.trim()) {
      filtered = templates.filter((t) =>
        matchesWordStartAny([t.name, t.template], searchTerm)
      );
    }
    setFilteredTemplates(filtered);
    setCurrentPage(1);
  }, [templates, searchTerm]);

  const handleAddTemplate = () => {
    setEditingTemplate(null);
    setFormData({ name: "", template: "" });
    setFormErrors({ name: "", template: "" });
    setShowModal(true);
  };

  const handleEditTemplate = async (templateId) => {
    try {
      const response = await settingsAPI.getTemplateById(templateId);
      if (response.success) {
        setEditingTemplate(response.data);
        setFormData({
          name: response.data.name || "",
          template: response.data.template || "",
        });
        setFormErrors({ name: "", template: "" });
        setShowModal(true);
      }
    } catch (error) {
      toast.error(error.message || "Failed to fetch template");
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm("Are you sure you want to delete this template?")) {
      return;
    }
    try {
      const response = await settingsAPI.deleteTemplate(templateId);
      if (response.success) {
        toast.success(response.message || "Template deleted successfully");
        fetchTemplates();
      }
    } catch (error) {
      toast.error(error.message || "Failed to delete template");
    }
  };

  const handleSaveTemplate = async (e) => {
    e.preventDefault();

    const errors = {
      name: !formData.name.trim() ? "Template name is required" : "",
      template: !formData.template.trim() ? "Template content is required" : "",
    };
    setFormErrors(errors);

    if (errors.name || errors.template) {
      return;
    }

    try {
      const templateData = {
        tempId: editingTemplate?.id || null,
        name: formData.name,
        tempContent: formData.template,
      };
      const response = await settingsAPI.storeFollowUpTemplate(templateData);
      if (response.success) {
        toast.success(
          editingTemplate
            ? "Template updated successfully"
            : "Template created successfully"
        );
        setShowModal(false);
        fetchTemplates();
      }
    } catch (error) {
      toast.error(error.message || "Failed to save template");
    }
  };

  const sortedFilteredTemplates = useMemo(() => {
    const list = [...filteredTemplates];
    const mul = sortConfig.direction === "asc" ? 1 : -1;
    list.sort((a, b) => {
      switch (sortConfig.key) {
        case "id":
          return mul * ((Number(a.id) || 0) - (Number(b.id) || 0));
        case "name":
          return mul * String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" });
        case "template":
          return mul * String(a.template || "").localeCompare(String(b.template || ""), undefined, { sensitivity: "base" });
        default:
          return 0;
      }
    });
    return list;
  }, [filteredTemplates, sortConfig]);

  const effectiveItemsPerPage =
    itemsPerPage === "all" ? sortedFilteredTemplates.length || 1 : (Number(itemsPerPage) || 10);
  const paginatedTemplates =
    itemsPerPage === "all"
      ? sortedFilteredTemplates
      : sortedFilteredTemplates.slice(
          (currentPage - 1) * effectiveItemsPerPage,
          currentPage * effectiveItemsPerPage
        );
  const totalPages = itemsPerPage === "all" ? 1 : Math.ceil(sortedFilteredTemplates.length / effectiveItemsPerPage);
  const entriesValue = String(itemsPerPage);

  /** When sorting by # (id) descending, show 3,2,1…; otherwise show 1,2,3… in current sort order. */
  const rowNumberForTemplateIndex = (rowIndex) => {
    const total = sortedFilteredTemplates.length;
    if (total === 0) return rowIndex + 1;
    const pos = (currentPage - 1) * effectiveItemsPerPage + rowIndex;
    if (sortConfig.key === "id" && sortConfig.direction === "desc") {
      return total - pos;
    }
    return pos + 1;
  };

  const columns = [
    {
      key: "rowNum",
      sortKey: "id",
      title: "#",
      sortable: true,
      className: "reusable-table-centered-col",
      render: (_row, rowIndex) => rowNumberForTemplateIndex(rowIndex),
    },
    { key: "name", title: "Template Name", sortable: true, render: (row) => row.name || "N/A" },
    {
      key: "template",
      title: "Content",
      sortable: true,
      className: "follow-up-col-template",
      render: (row) => {
        const content = row.template || "";
        if (!content) return "N/A";
        return <div className="follow-up-template-content-full">{content}</div>;
      },
    },
    {
      key: "action",
      title: "Action",
      className: "reusable-table-centered-col",
      render: (row) => (
        <ActionButtons
          onEdit={() => handleEditTemplate(row.id)}
          onDelete={() => handleDeleteTemplate(row.id)}
          hideView
        />
      ),
    },
  ];

  return (
    <section className="settings-section-card follow-up-setting">
      <h3 className="settings-section-card-title">Follow Up</h3>
      <div className="follow-up-header">
        <div className="search-container">
          <span className="search-label">Search:</span>
          <input
            type="text"
            className="search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button className="search-icon-btn" aria-label="Search">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              viewBox="0 0 16 16"
            >
              <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
            </svg>
          </button>
        </div>
        <button className="add-template-btn" onClick={handleAddTemplate}>
          Add Template
        </button>
      </div>

      {loading ? (
        <SkeletonLoader type="mediaRows" count={2} />
      ) : (
        <>
          <div className="follow-up-table-container">
            <Table
              columns={columns}
              data={paginatedTemplates}
              sortBy={sortConfig.key}
              sortOrder={sortConfig.direction}
              onSort={requestSort}
              isLoading={false}
              loadingMode="skeleton"
              skeletonRows={Math.min(effectiveItemsPerPage, 10)}
              emptyMessage="No templates found"
            />
          </div>

          <ListScreenPaginationBar
            entriesValue={entriesValue}
            onEntriesChange={(value) => {
              setItemsPerPage(value === "all" ? "all" : Number(value));
              setCurrentPage(1);
            }}
            currentPage={currentPage}
            totalPages={Math.max(totalPages || 1, 1)}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </>
      )}

      {showModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowModal(false)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: "white",
              padding: "30px",
              borderRadius: "8px",
              maxWidth: "600px",
              width: "90%",
              maxHeight: "80vh",
              overflow: "auto",
            }}
          >
            <h3 style={{ marginTop: 0 }}>
              {editingTemplate ? "Edit Template" : "Add Template"}
            </h3>
            <form onSubmit={handleSaveTemplate} noValidate>
              <div style={{ marginBottom: "15px" }}>
                <label style={{ display: "block", marginBottom: "5px" }}>
                  Template Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({ ...formData, name: e.target.value });
                    if (formErrors.name) {
                      setFormErrors((prev) => ({ ...prev, name: "" }));
                    }
                  }}
                  className={formErrors.name ? "input-error" : ""}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: formErrors.name ? "1px solid #dc2626" : "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                />
                {formErrors.name ? (
                  <div className="field-error">{formErrors.name}</div>
                ) : null}
              </div>
              <div style={{ marginBottom: "15px" }}>
                <label style={{ display: "block", marginBottom: "5px" }}>
                  Template Content *
                </label>
                <textarea
                  value={formData.template}
                  onChange={(e) => {
                    setFormData({ ...formData, template: e.target.value });
                    if (formErrors.template) {
                      setFormErrors((prev) => ({ ...prev, template: "" }));
                    }
                  }}
                  className={formErrors.template ? "input-error" : ""}
                  rows="10"
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: formErrors.template ? "1px solid #dc2626" : "1px solid #ddd",
                    borderRadius: "4px",
                    fontFamily: "inherit",
                  }}
                />
                {formErrors.template ? (
                  <div className="field-error">{formErrors.template}</div>
                ) : null}
              </div>
              <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{
                    padding: "8px 16px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    background: "white",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: "8px 16px",
                    border: "none",
                    borderRadius: "4px",
                    background: "#337ab7",
                    color: "white",
                    cursor: "pointer",
                  }}
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

// --- Calendar Permission Component ---
const CalendarPermissionSetting = () => {
  const [permissions, setPermissions] = useState([]);
  const [groupedPermissions, setGroupedPermissions] = useState({});
  const [caregivers, setCaregivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCaregivers, setSelectedCaregivers] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState({
    create: false,
    view: false,
    edit: false,
    delete: false,
  });
  const [editingGroupKey, setEditingGroupKey] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [caregiverSearch, setCaregiverSearch] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteGroupKey, setDeleteGroupKey] = useState(null);
  const [deleteCaregiverName, setDeleteCaregiverName] = useState("");
  const [isDeletingPermission, setIsDeletingPermission] = useState(false);

  const fetchData = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      const [permsRes, caregiversRes] = await Promise.all([
        settingsAPI.getCalendarPermissions(),
        settingsAPI.getCaregiversForPermission(),
      ]);

      if (permsRes.success) {
        setPermissions(permsRes.data || []);
        // Group permissions by caregiver_id and practice_id
        const grouped = {};
        permsRes.data.forEach((perm) => {
          const key =
            perm.practice_id !== null
              ? `${perm.caregiver_id}-${perm.practice_id}`
              : `${perm.caregiver_id}`;
          if (!grouped[key]) {
            grouped[key] = {
              caregiver_id: perm.caregiver_id,
              practice_id: perm.practice_id,
              caregiver_name: perm.caregiver_name,
              practice_name: perm.practice_name,
              created_by_name: perm.created_by_name,
              permissions: [],
            };
          }
          grouped[key].permissions.push(perm.permission_name);
        });
        setGroupedPermissions(grouped);
      }

      if (caregiversRes.success) {
        setCaregivers(caregiversRes.data || []);
      }
    } catch (error) {
      toast.error(error.message || "Failed to fetch data");
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isDropdownOpen &&
        !event.target.closest(".caregiver-dropdown-wrapper")
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleSavePermissions = async () => {
    if (isSaving) return;

    if (selectedCaregivers.length === 0) {
      toast.error("Please select at least one caregiver");
      return;
    }

    const permNames = Object.keys(selectedPermissions).filter(
      (key) => selectedPermissions[key]
    );
    if (permNames.length === 0) {
      toast.error("Please select at least one permission");
      return;
    }

    try {
      setIsSaving(true);
      const response = await settingsAPI.createCalendarPermission({
        caregivers: selectedCaregivers,
        permissions: permNames,
        groupKey: editingGroupKey,
      });

      if (response.success) {
        toast.success(response.message || "Permissions saved successfully!");
        setSelectedCaregivers([]);
        setSelectedPermissions({ create: false, view: false, edit: false, delete: false });
        setEditingGroupKey(null);
        fetchData({ silent: true });
      }
    } catch (error) {
      toast.error(error.message || "Failed to save permissions");
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditPermissions = (groupKey) => {
    const group = groupedPermissions[groupKey];
    if (group) {
      const normalizedPermissions = (group.permissions || []).map((p) =>
        String(p || "").toLowerCase()
      );
      setSelectedCaregivers([String(group.caregiver_id)]);
      setEditingGroupKey(groupKey);
      setSelectedPermissions({
        create: normalizedPermissions.includes("create"),
        view: normalizedPermissions.includes("view"),
        edit: normalizedPermissions.includes("edit"),
        delete: normalizedPermissions.includes("delete"),
      });
      setIsDropdownOpen(false);
      setCaregiverSearch("");
    }
  };

  const openDeletePermissionModal = (groupKey, caregiverName = "") => {
    setDeleteGroupKey(groupKey);
    setDeleteCaregiverName(caregiverName || "this system caregiver");
    setShowDeleteModal(true);
  };

  const closeDeletePermissionModal = (force = false) => {
    if (isDeletingPermission && !force) return;
    setShowDeleteModal(false);
    setDeleteGroupKey(null);
    setDeleteCaregiverName("");
  };

  const handleDeletePermissions = async () => {
    if (!deleteGroupKey) return;
    try {
      setIsDeletingPermission(true);
      const response = await settingsAPI.deleteCalendarPermission(deleteGroupKey);
      if (response.success) {
        toast.success(response.message || "Permission deleted successfully");
        closeDeletePermissionModal(true);
        fetchData({ silent: true });
      }
    } catch (error) {
      toast.error(error.message || "Failed to delete permissions");
    } finally {
      setIsDeletingPermission(false);
    }
  };

  const filteredGroups = Object.keys(groupedPermissions).filter((key) => {
    if (!searchTerm.trim()) return true;
    const group = groupedPermissions[key];
    return matchesWordStartAny(
      [group.caregiver_name, group.practice_name],
      searchTerm
    );
  });

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const filteredCaregivers = caregivers.filter((cg) => {
    if (!caregiverSearch.trim()) return true;
    const practice = cg.practice_name ? ` ${cg.practice_name}` : "";
    return matchesWordStart(`${cg.name}${practice}`, caregiverSearch);
  });

  const tableData = filteredGroups.map((key) => ({
    _groupKey: key,
    ...groupedPermissions[key],
  }));

  const PERMISSION_ORDER = ["Create", "View", "Edit", "Delete"];
  const sortPermissionsByOrder = (permissions = []) => {
    const unique = Array.from(new Set(permissions));
    return unique.sort((a, b) => {
      const idxA = PERMISSION_ORDER.indexOf(a);
      const idxB = PERMISSION_ORDER.indexOf(b);
      const safeA = idxA === -1 ? Number.MAX_SAFE_INTEGER : idxA;
      const safeB = idxB === -1 ? Number.MAX_SAFE_INTEGER : idxB;
      return safeA - safeB;
    });
  };

  const sortedTableData = useMemo(() => {
    if (!sortConfig.key) {
      return tableData;
    }
    const list = [...tableData];
    const mul = sortConfig.direction === "asc" ? 1 : -1;
    list.sort((a, b) => {
      switch (sortConfig.key) {
        case "rowNo":
          return mul * ((Number(a.caregiver_id) || 0) - (Number(b.caregiver_id) || 0));
        case "caregiver_name":
          return mul * String(a.caregiver_name || "").localeCompare(String(b.caregiver_name || ""), undefined, { sensitivity: "base" });
        case "permissions":
          return mul * sortPermissionsByOrder(a.permissions || []).join(",").localeCompare(
            sortPermissionsByOrder(b.permissions || []).join(","),
            undefined,
            { sensitivity: "base" }
          );
        case "created_by_name":
          return mul * String(a.created_by_name || "").localeCompare(String(b.created_by_name || ""), undefined, { sensitivity: "base" });
        default:
          return 0;
      }
    });
    return list;
  }, [tableData, sortConfig]);

  const columns = [
    {
      key: "rowNo",
      title: "#",
      sortable: true,
      sortKey: "rowNo",
      className: "reusable-table-centered-col",
      render: (_row, rowIndex) => rowIndex + 1,
    },
    { key: "caregiver_name", title: "System Caregiver", sortable: true, render: (row) => row.caregiver_name || "N/A" },
    {
      key: "permissions",
      title: "Permissions",
      sortable: true,
      render: (row) =>
        sortPermissionsByOrder(row.permissions || []).map((perm) => (
          <span key={perm} className={`permission-badge ${perm.toLowerCase()}`}>
            {perm}
          </span>
        )),
    },
    { key: "created_by_name", title: "Created By", sortable: true, render: (row) => row.created_by_name || "N/A" },
    {
      key: "actions",
      title: "Actions",
      className: "reusable-table-centered-col",
      render: (row) => (
        <ActionButtons
          onEdit={() => handleEditPermissions(row._groupKey)}
          onDelete={() => openDeletePermissionModal(row._groupKey, row.caregiver_name)}
          isDeleting={isDeletingPermission && deleteGroupKey === row._groupKey}
          hideView
        />
      ),
    },
  ];

  return (
    <section className="settings-section-card calendar-permission-setting">
      <h3 className="settings-section-card-title">Calendar Permission</h3>

      <div className="add-caregiver-form">
        <div className="caregiver-controls-row">
          <div className="caregiver-info-group">
            <label htmlFor="caregiver-select">
              System Caregiver <span className="required">*</span>
            </label>
            <div className="caregiver-dropdown-wrapper">
              <div
                className="caregiver-select-box"
                onClick={() => setIsDropdownOpen(true)}
              >
                <input
                  id="caregiver-select"
                  type="text"
                  className="caregiver-select-input"
                  value={isDropdownOpen ? caregiverSearch : (
                    selectedCaregivers.length === 0
                      ? ""
                      : selectedCaregivers.length === 1
                        ? (caregivers.find((cg) => String(cg.id) === String(selectedCaregivers[0]))?.name || "Selected")
                        : `${selectedCaregivers.length} caregivers selected`
                  )}
                  onChange={(e) => {
                    setCaregiverSearch(e.target.value);
                    if (!isDropdownOpen) setIsDropdownOpen(true);
                  }}
                  onFocus={() => setIsDropdownOpen(true)}
                  placeholder="Search caregivers..."
                />
                <span className="caregiver-dropdown-arrow">▼</span>
              </div>
              {isDropdownOpen && (
                <div className="caregiver-dropdown-list">
                  {filteredCaregivers.length === 0 ? (
                    <div className="caregiver-dropdown-item disabled">
                      No caregivers found
                    </div>
                  ) : (
                    filteredCaregivers.map((cg) => {
                      const isSelected = selectedCaregivers.map(String).includes(String(cg.id));
                      return (
                        <div
                          key={cg.id}
                          className={`caregiver-dropdown-item ${isSelected ? "selected" : ""}`}
                          onClick={() => {
                            if (isSelected) {
                              setSelectedCaregivers(
                                selectedCaregivers.filter((id) => String(id) !== String(cg.id))
                              );
                            } else {
                              setSelectedCaregivers([...selectedCaregivers, String(cg.id)]);
                            }
                            setCaregiverSearch("");
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => { }}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span>
                            {cg.name} {cg.practice_name ? `(${cg.practice_name})` : ""}
                          </span>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="permission-checkboxes">
            <label>
              <input
                type="checkbox"
                checked={selectedPermissions.create}
                onChange={(e) =>
                  setSelectedPermissions({
                    ...selectedPermissions,
                    create: e.target.checked,
                  })
                }
              />{" "}
              Create
            </label>
            <label>
              <input
                type="checkbox"
                checked={selectedPermissions.view}
                onChange={(e) =>
                  setSelectedPermissions({
                    ...selectedPermissions,
                    view: e.target.checked,
                  })
                }
              />{" "}
              View
            </label>
            <label>
              <input
                type="checkbox"
                checked={selectedPermissions.edit}
                onChange={(e) =>
                  setSelectedPermissions({
                    ...selectedPermissions,
                    edit: e.target.checked,
                  })
                }
              />{" "}
              Edit
            </label>
            <label>
              <input
                type="checkbox"
                checked={selectedPermissions.delete}
                onChange={(e) =>
                  setSelectedPermissions({
                    ...selectedPermissions,
                    delete: e.target.checked,
                  })
                }
              />{" "}
              Delete
            </label>
          </div>

          <div className="search-container" style={{ marginLeft: "auto" }}>
            <span className="search-label">Search:</span>
            <input
              type="text"
              className="search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button className="search-icon-btn" aria-label="Search">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                viewBox="0 0 16 16"
              >
                <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
              </svg>
            </button>
          </div>

          <div className="save-btn-wrapper">
            <button className="save-btn" onClick={handleSavePermissions} disabled={isSaving}>
              {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>



      {loading ? (
        <SkeletonLoader type="mediaRows" count={2} />
      ) : (
        <div className="calendar-table-container">
          <Table
            columns={columns}
            data={sortedTableData}
            sortBy={sortConfig.key}
            sortOrder={sortConfig.direction}
            onSort={requestSort}
            isLoading={false}
            loadingMode="skeleton"
            skeletonRows={8}
            emptyMessage="No permissions found"
          />
        </div>
      )}

      {showDeleteModal && (
        <div className="practices-list-modal-overlay">
          <div className="practices-list-modal-container">
            <div className="practices-list-modal-header">
              <h5 className="practices-list-modal-title">Confirm Delete</h5>
              <button
                type="button"
                className="practices-list-modal-close"
                onClick={closeDeletePermissionModal}
              >
                ×
              </button>
            </div>
            <div className="practices-list-modal-body">
              <p>
                Are you sure you want to delete calendar permissions for{" "}
                <strong>{deleteCaregiverName}</strong>?
              </p>
              <p className="practices-list-modal-warning">
                This action cannot be undone.
              </p>
            </div>
            <div className="practices-list-modal-footer">
              <button
                type="button"
                className="practices-list-btn-cancel"
                onClick={closeDeletePermissionModal}
                disabled={isDeletingPermission}
              >
                Cancel
              </button>
              <button
                type="button"
                className="practices-list-btn-delete"
                onClick={handleDeletePermissions}
                disabled={isDeletingPermission}
              >
                {isDeletingPermission ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="list-screen-me-2" />
                    Deleting...
                  </>
                ) : (
                  "Delete"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

// --- Main Settings Component ---
const Settings = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth || {});
  const { updateBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tabs = useSettingsTabs();
  const isSystemCaregiver = user?.role_id === 7;
  const isPracticeCaregiver = user?.role_id === 5;
  const hideCalendarPermissionTab = isSystemCaregiver || isPracticeCaregiver;
  const cachedAccountSnapshot = (() => {
    try {
      const raw = localStorage.getItem("user");
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const user = parsed?.user || parsed;
      if (!user) return null;
      return {
        full_name: `${user.last_name || ""} ${user.first_name || ""}`.trim() || "Not Available",
        phone: user.phone || "Not Available",
        username: user.username || "Not Available",
        fax: user.fax || "Not Available",
        email: user.email || "Not Available",
        session_time: user.session_time || 30,
        is_two_way_auth: user.is_two_way_auth || 0,
        ai_based_analysis_enabled: user.ai_based_analysis_enabled == null ? 1 : Number(user.ai_based_analysis_enabled)
      };
    } catch (_) {
      return null;
    }
  })();
  const requestedTab = searchParams.get("tab") || SETTINGS_TAB_IDS.ACCOUNT;
  const activeTab = tabs.some((t) => t.id === requestedTab)
    ? requestedTab
    : (tabs[0]?.id || SETTINGS_TAB_IDS.ACCOUNT);
  const [accountData, setAccountData] = useState(cachedAccountSnapshot);
  const [accountLoaded, setAccountLoaded] = useState(false);
  const [sessionTime, setSessionTime] = useState(cachedAccountSnapshot?.session_time || 60);
  const [twoWayAuthEnabled, setTwoWayAuthEnabled] = useState(cachedAccountSnapshot?.is_two_way_auth === 1);
  const [aiBasedAnalysisEnabled, setAiBasedAnalysisEnabled] = useState(
    cachedAccountSnapshot?.ai_based_analysis_enabled !== 0
  );

  useEffect(() => {
    if (requestedTab === SETTINGS_TAB_IDS.PERMISSION) {
      navigate("/permission/roles", { replace: true });
      return;
    }
    if (requestedTab !== activeTab) {
      navigate(`/settings?tab=${encodeURIComponent(activeTab)}`, { replace: true });
    }
  }, [requestedTab, activeTab, navigate]);

  useEffect(() => {
    updateBreadcrumbs([
      { label: "Settings", path: "/settings", active: true }
    ]);
    return () => {
      clearBreadcrumbs();
    };
  }, [updateBreadcrumbs, clearBreadcrumbs]);

  useEffect(() => {
    if (activeTab !== SETTINGS_TAB_IDS.ACCOUNT || accountLoaded) return;
    fetchAccountSettings();
  }, [activeTab, accountLoaded]);

  const fetchAccountSettings = async () => {
    try {
      const response = await settingsAPI.getUserAccountSettings();
      if (response.success) {
        setAccountData(response.data);
        setSessionTime(response.data.session_time || 60);
        setTwoWayAuthEnabled(response.data.is_two_way_auth === 1);
        const aiEnabled = response.data.ai_based_analysis_enabled !== 0 ? 1 : 0;
        setAiBasedAnalysisEnabled(aiEnabled === 1);
        dispatch(updateUserAiBasedAnalysis(aiEnabled));
        setAccountLoaded(true);
      }
    } catch (error) {
      toast.error(error.message || "Failed to fetch account settings");
    }
  };

// --- Main Settings Component ---
  const handleUpdateSession = async () => {
    try {
      const response = await settingsAPI.updateSessionSettings(sessionTime);
      if (response.success) {
        toast.success(response.message || "Session time updated successfully");
        dispatch(updateUserSessionTime(sessionTime));
        fetchAccountSettings();
      }
    } catch (error) {
      toast.error(error.message || "Failed to update session time");
    }
  };

  const toggleTwoWayAuth = async () => {
    const newStatus = twoWayAuthEnabled ? 0 : 1;
    try {
      const response = await settingsAPI.toggleTwoWayAuth(newStatus);
      if (response.success) {
        toast.success(response.message || "Two-way authentication updated");
        setTwoWayAuthEnabled(newStatus === 1);
        fetchAccountSettings();
      }
    } catch (error) {
      toast.error(error.message || "Failed to toggle two-way authentication");
    }
  };

  const toggleAiBasedAnalysis = async () => {
    const newStatus = aiBasedAnalysisEnabled ? 0 : 1;
    try {
      const response = await settingsAPI.toggleAiBasedAnalysis(newStatus);
      if (response.success) {
        toast.success(response.message || "AI Based Analysis updated");
        setAiBasedAnalysisEnabled(newStatus === 1);
        dispatch(updateUserAiBasedAnalysis(newStatus));
        fetchAccountSettings();
      }
    } catch (error) {
      toast.error(error.message || "Failed to toggle AI based analysis");
    }
  };

  const handleClearCache = async () => {
    try {
      const response = await settingsAPI.clearCache();
      if (response.success) {
        toast.success(response.message || "Cache cleared successfully");
      }
    } catch (error) {
      toast.error(error.message || "Failed to clear cache");
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case "account":
        if (!accountData) {
          return <div style={{ textAlign: "center", padding: "40px" }}>No data available</div>;
        }
        return (
          <section className="settings-section-card">
            <h3 className="settings-section-card-title">Account Settings</h3>
            <div className="account-grid">
              {/* Row 1: Left - Full Name, Right - Phone */}
              <div className="label">Full Name</div>
              <div className="sep">:</div>
              <div className="value" style={{ display: 'flex', alignItems: 'center', height: '36px' }}>{accountData.full_name || "Not Available"}</div>

              <div className="label">Phone</div>
              <div className="sep">:</div>
              <div className="value" style={{ display: 'flex', alignItems: 'center', height: '36px' }}>{formatPhoneForDisplay(accountData.phone, "Not Available")}</div>

              {/* Row 2: Left - Username, Right - Fax */}
              <div className="label">Username</div>
              <div className="sep">:</div>
              <div className="value" style={{ display: 'flex', alignItems: 'center', height: '36px' }}>{accountData.username || "Not Available"}</div>

              <div className="label">Fax</div>
              <div className="sep">:</div>
              <div className="value" style={{ display: 'flex', alignItems: 'center', height: '36px' }}>{formatPhoneForDisplay(accountData.fax, "Not Available")}</div>

              {/* Row 3: Left - Email, Right - Two Way Authentication */}
              <div className="label" style={{ display: 'flex', alignItems: 'center', height: '36px' }}>Email</div>
              <div className="sep">:</div>
              <div className="value" style={{ display: 'flex', alignItems: 'center', height: '36px' }}>{accountData.email || "Not Available"}</div>

              <div className="label" style={{ display: 'flex', alignItems: 'center', height: '36px' }}>Two Way Authentication</div>
              <div className="sep" style={{ display: 'flex', alignItems: 'center', height: '36px', justifyContent: 'center' }}>:</div>
              <div className="value" style={{ display: 'flex', alignItems: 'center', height: '36px', justifyContent: 'flex-start' }}>
                <button
                  className={`badge ${twoWayAuthEnabled ? "enabled" : "disabled"}`}
                  onClick={toggleTwoWayAuth}
                  style={{ margin: 0 }}
                >
                  {twoWayAuthEnabled ? "Enabled" : "Disabled"}
                </button>
              </div>

              {/* Row 4: Current Session Time | AI Based Analysis */}
              <div className="label" style={{ display: 'flex', alignItems: 'center', height: '36px' }}>Current Session Time</div>
              <div className="sep" style={{ display: 'flex', alignItems: 'center', height: '36px', justifyContent: 'center' }}>:</div>
              <div className="value" style={{ display: 'flex', alignItems: 'center', height: '36px' }}>{accountData.session_time || 60} minutes</div>

              <div className="label" style={{ display: 'flex', alignItems: 'center', height: '36px' }}>AI Based Analysis</div>
              <div className="sep" style={{ display: 'flex', alignItems: 'center', height: '36px', justifyContent: 'center' }}>:</div>
              <div className="value" style={{ display: 'flex', alignItems: 'center', height: '36px', justifyContent: 'flex-start' }}>
                <button
                  type="button"
                  className={`badge ${aiBasedAnalysisEnabled ? "enabled" : "disabled"}`}
                  onClick={toggleAiBasedAnalysis}
                  style={{ margin: 0 }}
                >
                  {aiBasedAnalysisEnabled ? "Enabled" : "Disabled"}
                </button>
              </div>

              {/* Row 5: Clear Cache */}
              <div className="label" style={{ display: 'flex', alignItems: 'center', height: '36px' }}>Clear Cache</div>
              <div className="sep" style={{ display: 'flex', alignItems: 'center', height: '36px', justifyContent: 'center' }}>:</div>
              <div className="value" style={{ display: 'flex', alignItems: 'center', height: '36px', justifyContent: 'flex-start' }}>
                <button className="clear-cache-btn" onClick={handleClearCache} style={{ margin: 0 }}>
                  Refresh
                </button>
              </div>

              {/* Row 6: Session Time (input + Update) */}
              <div className="label">Session Time (minutes)</div>
              <div className="sep">:</div>
              <div className="value session-control">
                <input
                  type="number"
                  className="session-input"
                  min={1}
                  value={sessionTime || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSessionTime(val === "" ? "" : parseInt(val));
                  }}
                  placeholder="Enter minutes"
                />
                <button 
                  className="update-btn" 
                  onClick={() => {
                    const timeToUpdate = Number(sessionTime);
                    if (!timeToUpdate || timeToUpdate < 1) {
                      toast.error("Please enter a valid session time (min 1 minute)");
                      return;
                    }
                    handleUpdateSession();
                  }}
                >
                  Update
                </button>
              </div>
            </div>
          </section>
        );
      case SETTINGS_TAB_IDS.SIDEBAR:
        return <SidebarMenuSettings />;
      case SETTINGS_TAB_IDS.FOLLOWUP:
        return <FollowUpSetting />;
      case SETTINGS_TAB_IDS.CALENDAR:
        return hideCalendarPermissionTab ? null : <CalendarPermissionSetting />;
      case SETTINGS_TAB_IDS.CHAT_ACCESS:
        return (
          <section className="settings-section-card settings-section-card--flush">
            <h3 className="settings-section-card-title">Chat Access</h3>
            <ChatAccess embedded />
          </section>
        );
      case SETTINGS_TAB_IDS.EMAIL_LOGS:
        return (
          <section className="settings-section-card settings-section-card--flush">
            <h3 className="settings-section-card-title">Email Logs</h3>
            <EmailLogs embedded />
          </section>
        );
      case SETTINGS_TAB_IDS.USER_LOGS:
        return (
          <section className="settings-section-card settings-section-card--flush">
            <h3 className="settings-section-card-title">User Logs</h3>
            <UserLogs embedded />
          </section>
        );
      case SETTINGS_TAB_IDS.MOBILE_APP:
        return (
          <section className="settings-section-card settings-section-card--flush">
            <h3 className="settings-section-card-title">Mobile App</h3>
            <MobileAppAccess embedded />
          </section>
        );
      case SETTINGS_TAB_IDS.USER_ROLES:
        return (
          <section className="settings-section-card settings-section-card--flush">
            <h3 className="settings-section-card-title">User Roles</h3>
            <UserRoleManagement embedded />
          </section>
        );
      default:
        return null;
    }
  };

  return (
    <SettingsHub activeTab={activeTab}>
      {renderContent()}
    </SettingsHub>
  );
};

export default Settings;
